import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Star, MapPin, Clock, Users, PawPrint, ArrowLeft, ArrowRight, Dog, Send, Heart, Navigation, Check, Loader2, Mail, Phone, Globe, ShieldCheck, Instagram, Facebook } from "lucide-react";
import { formatDistanceToNow, formatDistanceToNowStrict, format } from "date-fns";
import { toast } from "sonner";
import { api } from "../lib/api";
import WeatherWidget from "../components/WeatherWidget";
import CheckInDrawer from "../components/CheckInDrawer";
import JsonLd from "../components/JsonLd";
import Breadcrumbs from "../components/Breadcrumbs";
import SignupPromptModal from "../components/SignupPromptModal";
import GoogleReviewsSection from "../components/GoogleReviewsSection";
import QuickReviewForm, { PawRating } from "../components/QuickReviewForm";
import ReviewCard from "../components/ReviewCard";
import ShareButton from "../components/ShareButton";
import PostCheckinSharePrompt from "../components/PostCheckinSharePrompt";
import CommunityBadge from "../components/CommunityBadge";
import { markPhotoLoaded } from "../lib/photoCache";
import { isOpenNow, todayHours } from "../components/ParkCard";
import { useAuth } from "../lib/auth";
import { track } from "../lib/analytics";
import { CHECKIN_STYLE, VERIFIABLE_CATEGORIES, getCategory } from "../lib/categories";
import { placeUrl } from "../lib/placeUrl";
import { useNoIndex } from "../lib/useNoIndex";
import { isPlaceIndexable } from "../lib/seoIndexRules";
import { cityFromPlace, cityUrl, cityCategoryUrl } from "../lib/cities";
import { Avatar } from "../components/Avatar";
import { ClosedNotice } from "../components/ClosedNotice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = "https://pawground.app";
// Photo URL resolver. When `ref` is an absolute path (starts with `/`)
// or full URL it's already a usable URL — pass through. Otherwise it's
// a Google Places photo_reference token that we route through the
// backend proxy.
const photoUrl = (ref, w = 1200) => {
  if (!ref) return null;
  if (ref.startsWith("http") || ref.startsWith("/")) return ref;
  return `${BACKEND_URL}/api/photo?ref=${encodeURIComponent(ref)}&w=${w}`;
};

// Maps our internal place_type to the most appropriate schema.org @type.
// Falls back to LocalBusiness for the generic case.
const SCHEMA_TYPE_BY_CATEGORY = {
  park: "Park",
  beach: ["Place", "TouristAttraction"],
  trail: ["Place", "TouristAttraction"],
  restaurant: "Restaurant",
  vet: "VeterinaryCare",
  groomer: ["LocalBusiness", "PetStore"],
  boarding: "LodgingBusiness",
  trainer: "LocalBusiness",
  store: "PetStore",
};

// Street part of a saved address ("1218 Main St Ste 1, Brandon, FL 33511, USA"
// → "1218 Main St Ste 1"). Null when the address has no street (e.g. "Brandon, FL").
const streetPart = (address, city) => {
  const parts = (address || "").split(",").map((s) => s.trim()).filter(Boolean);
  const cityIdx = city ? parts.findIndex((s) => s.toLowerCase() === city.toLowerCase()) : -1;
  if (cityIdx > 0) return parts.slice(0, cityIdx).join(", ");
  return parts.length > 1 && /\d/.test(parts[0]) ? parts[0] : null;
};

const buildPlaceSchema = (park, { reviews = [], lastModifiedIso = null } = {}) => {
  if (!park) return null;
  const schemaType = SCHEMA_TYPE_BY_CATEGORY[park.place_type] || "LocalBusiness";
  const imageUrl = park.photos?.[0]?.ref ? photoUrl(park.photos[0].ref, 1200) : `${SITE_URL}/icon-512.png`;
  // Canonical URL: prefer the Fix #2 /{cat}/{city}/{slug} form when
  // we have all the parts, else fall back to the appropriate legacy
  // path. `placeUrl` returns a relative path — prepend SITE_URL for
  // JSON-LD @id/url which want absolute URIs.
  const canonicalUrl = `${SITE_URL}${placeUrl(park)}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": schemaType,
    "@id": canonicalUrl,
    name: park.name,
    url: canonicalUrl,
    image: imageUrl,
  };
  if (park.address) {
    // For delivery-only listings, omit `streetAddress` entirely — the
    // stored address is a third-party-sourced, unverified value we
    // never render on the visible page (see visit_type spec in
    // models/parks.py). Emitting it in JSON-LD anyway would let Google
    // index a residential address as a visitable storefront, exactly
    // what the visible-content guard was designed to prevent. Structured
    // data must MATCH visible content — city + region only is a valid
    // partial PostalAddress and correctly represents "operates from
    // this city" without asserting a walk-in point.
    const isDeliveryOnly = park.visit_type === "delivery_only";
    const street = isDeliveryOnly ? null : streetPart(park.address, park.city);
    schema.address = {
      "@type": "PostalAddress",
      ...(street ? { streetAddress: street } : {}),
      ...(park.city ? { addressLocality: park.city } : {}),
      ...(park.state ? { addressRegion: park.state } : {}),
      ...(park.zip ? { postalCode: park.zip } : {}),
      addressCountry: park.state === "PR" ? "PR" : "US",
    };
  }
  if (Number.isFinite(park.lat) && Number.isFinite(park.lng)) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude: park.lat,
      longitude: park.lng,
    };
  }
  // AggregateRating + Review[] are derived from PAWGROUND user reviews
  // ONLY — not the Google rating. Spec is explicit on this so users
  // see review-stars in SERPs that match what we actually publish on
  // the page, not Google's rating they can already see elsewhere.
  if (Array.isArray(reviews) && reviews.length > 0) {
    const ratings = reviews
      .map((r) => Number(r.rating))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ratings.length > 0) {
      const avg = ratings.reduce((s, v) => s + v, 0) / ratings.length;
      schema.aggregateRating = {
        "@type": "AggregateRating",
        // Round to 1 decimal so search engines have a clean number.
        ratingValue: Math.round(avg * 10) / 10,
        reviewCount: ratings.length,
        bestRating: 5,
        worstRating: 1,
      };
    }
    // Up to 5 most-recent reviews by created_at desc. Slice AFTER
    // sorting so we don't waste cycles serializing reviews we'll drop.
    const recent = [...reviews]
      .filter((r) => r && (r.text || r.rating))
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 5);
    if (recent.length > 0) {
      schema.review = recent.map((r) => ({
        "@type": "Review",
        author: {
          "@type": "Person",
          name: r.owner_name || "Pawground member",
        },
        // ISO 8601. created_at is already ISO from the backend, but
        // defensively re-stringify so a non-ISO storage shape can't
        // poison the schema.
        datePublished: r.created_at
          ? new Date(r.created_at).toISOString()
          : new Date().toISOString(),
        reviewRating: {
          "@type": "Rating",
          ratingValue: Number(r.rating) || 0,
          bestRating: 5,
          worstRating: 1,
        },
        // reviewBody is optional in Schema.org; emit only when present
        // so quick-tap-only reviews (no text) don't ship an empty string.
        ...(r.text && r.text.trim() ? { reviewBody: r.text.trim() } : {}),
      }));
    }
  }
  if (lastModifiedIso) {
    schema.dateModified = lastModifiedIso;
  }
  // Service-category sub-rating breakdown for crawlers. Only emit
  // when (a) the place is a vet/groomer/boarding/trainer and (b) at
  // least one review carries the sub-rating for that dimension. Each
  // dimension is independent — missing data means we skip the
  // PropertyValue rather than emit a fake zero. Averages round to 1
  // decimal to match the existing aggregateRating format.
  const SERVICE_TYPES_FOR_SCHEMA = ["vet", "groomer", "boarding", "trainer"];
  if (
    SERVICE_TYPES_FOR_SCHEMA.includes(park.place_type) &&
    Array.isArray(reviews) &&
    reviews.length > 0
  ) {
    const avg = (key) => {
      const vals = reviews
        .map((r) => Number(r?.[key]))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (vals.length === 0) return null;
      return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10;
    };
    const dimensions = [
      { key: "price_rating", name: "Price / Value" },
      { key: "staff_rating", name: "Staff & Care" },
      { key: "wait_rating",  name: "Wait Time" },
    ];
    const additionalProperty = dimensions
      .map((d) => ({ name: d.name, value: avg(d.key) }))
      .filter((d) => d.value !== null)
      .map((d) => ({
        "@type": "PropertyValue",
        name: d.name,
        value: String(d.value),
      }));
    if (additionalProperty.length > 0) {
      schema.additionalProperty = additionalProperty;
    }
  }
  if (park.phone) schema.telephone = park.phone;
  if (park.website) schema.sameAs = [park.website];
  return schema;
};

const DAYS = [
  ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"],
  ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"],
];

const BUSY_OPTIONS = [
  { key: "quiet", label: "Quiet", paws: "🐾" },
  { key: "moderate", label: "Moderate", paws: "🐾🐾" },
  { key: "busy", label: "Busy", paws: "🐾🐾🐾" },
];

const BusyReport = ({ park, user, onReported, onPromptSignup }) => {
  const [submitting, setSubmitting] = useState(null);

  const current = park.busy_level;
  const reportedAt = park.busy_reported_at;

  const pickLevel = async (level) => {
    setSubmitting(level);
    try {
      await api.post(`/parks/${park.id}/busy-report`, { level });
      toast.success("Thanks — others can see this now!");
      onReported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your report");
    } finally {
      setSubmitting(null);
    }
  };

  // --- Logged-out: render the same 3-button layout but every tap
  //     opens the signup prompt modal instead of submitting. Mirrors
  //     the logged-in shape so the gated CTA feels native rather
  //     than a separate sign-in panel. ---------------------------------
  if (!user) {
    return (
      <div
        data-testid="busy-report-widget"
        className="bg-brand-light-green rounded-2xl p-4 sm:p-5 border border-brand-forest/20"
      >
        <div className="flex items-baseline justify-between gap-3">
          <div className="font-heading text-base font-bold text-brand-ink">How busy is it?</div>
          {current && reportedAt && (
            <div className="text-xs text-brand-ink/70" data-testid="busy-report-latest">
              Reported as{" "}
              <span className="font-semibold text-brand-forest capitalize">{current}</span>
              {" · "}
              {formatDistanceToNow(new Date(reportedAt), { addSuffix: false })} ago
            </div>
          )}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="busy-report-buttons">
          {BUSY_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              data-testid={`busy-option-${opt.key}`}
              onClick={() => onPromptSignup?.("busy_report")}
              className="bg-white border border-brand-border-warm rounded-2xl px-3 py-2.5 text-center text-sm font-semibold text-brand-ink hover:border-brand-forest hover:shadow-warm transition-all"
            >
              <span className="text-lg leading-none block" aria-hidden>{opt.paws}</span>
              <span className="mt-1 block">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // --- Logged-in: simple 3-button reporter (Quiet / Moderate / Busy) ----------
  return (
    <div
      data-testid="busy-report-widget"
      className="bg-brand-light-green rounded-2xl p-4 sm:p-5 border border-brand-forest/20"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-heading text-base font-bold text-brand-ink">How busy is it?</div>
        {current && reportedAt && (
          <div className="text-xs text-brand-ink/70" data-testid="busy-report-latest">
            Reported as{" "}
            <span className="font-semibold text-brand-forest capitalize">{current}</span>
            {" · "}
            {formatDistanceToNow(new Date(reportedAt), { addSuffix: false })} ago
          </div>
        )}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2" data-testid="busy-report-buttons">
        {BUSY_OPTIONS.map((opt) => {
          const active = current === opt.key;
          const isLoading = submitting === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              data-testid={`busy-option-${opt.key}`}
              onClick={() => pickLevel(opt.key)}
              disabled={!!submitting}
              aria-pressed={active}
              className={[
                "flex flex-col items-center justify-center rounded-2xl py-3 px-2 text-center transition-all border-2 active:scale-[0.97] disabled:opacity-60",
                active
                  ? "bg-brand-forest text-white border-brand-forest shadow-warm"
                  : "bg-white text-brand-ink border-transparent hover:border-brand-forest/40",
              ].join(" ")}
            >
              <span className="text-lg leading-none" aria-hidden="true">{opt.paws}</span>
              <span className="font-semibold text-sm mt-1.5">{opt.label}</span>
              {isLoading && <Loader2 className="w-3 h-3 animate-spin mt-1.5" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};



const ParkDetail = () => {
  // Route params — three shapes now supported:
  //   /park/:id                              (legacy Google place URL)
  //   /place/:slug                           (community/manual place URL)
  //   /:categorySlug/:citySlug/:placeSlug    (Fix #2 canonical URL)
  // For the third we re-fetch the doc by place_id, so ParkDetail needs
  // to resolve the id from whichever param path we came in on.
  const {
    id: idParam,
    slug: slugParam,
    placeSlug: placeSlugParam,
  } = useParams();
  const isCommunityRoute = !!slugParam && !placeSlugParam;
  // Fix #2: canonical URL carries the slug in the LAST segment. The
  // slug's `-<last6>` suffix is derived from the place_id, so we can
  // recover the id from it AT INGEST — but for pre-existing pages we
  // must ask the backend to resolve slug → id via a lookup. Rather
  // than adding a new endpoint, we let the canonical route pass the
  // slug through and the backend's `get_park` accepts either an id
  // OR a slug (see Fix #2 backend routes).
  const id = isCommunityRoute
    ? `sp_${slugParam}`
    : placeSlugParam
    ? placeSlugParam  // canonical URL — treat as slug/id lookup
    : idParam;
  const navigate = useNavigate();
  const location = useLocation();

  // Legacy id redirect — old shared `/park/manual_<X>` URLs map to
  // the rebranded `/park/<X>` form. We do this on the client so the
  // address bar updates (Googlebot sees the canonical URL and stops
  // indexing the legacy form). The backend's place_redirects table
  // covers the same ground server-side for direct /api/places/ calls.
  useEffect(() => {
    if (!isCommunityRoute && idParam && idParam.startsWith("manual_")) {
      const cleanId = idParam.slice("manual_".length);
      navigate(`/park/${cleanId}`, { replace: true });
    }
  }, [idParam, isCommunityRoute, navigate]);

  // Fix #2 Step 3: canonical URL redirect for legacy /park/{id} hits.
  // When the user lands on /park/{place_id} we ask the backend for the
  // canonical /{cat}/{city}/{slug} URL and location.replace() to it.
  // Runs BEFORE the full detail payload loads so the address bar
  // updates on first paint. If no canonical URL exists (missing slug,
  // or the doc isn't a Google place) we stay on /park/{id} — the old
  // route still resolves.
  useEffect(() => {
    if (isCommunityRoute) return;
    if (!idParam || idParam.startsWith("manual_")) return;
    // Only fire for legacy URLs — canonical URLs already carry the
    // slug in the last segment, no further redirect needed.
    if (placeSlugParam) return;
    // Never redirect if the URL is `/park/{id}` where id is a slug —
    // that shape gets normalized by the backend, not by another
    // client-side hop.
    api
      .get(`/places/canonical-url?id=${encodeURIComponent(idParam)}`)
      .then((r) => {
        const url = r?.data?.url;
        if (url && url !== location.pathname) {
          // Real replace() so bookmarks + browser history rewrite
          // to the canonical form. This is the client-side equivalent
          // of a 301 — search-engine signal via the static shell's
          // meta-refresh + canonical tag (baked at prerender time).
          navigate(url, { replace: true });
        }
      })
      .catch(() => {
        // Redirect optional — silently stay on legacy URL on any error.
      });
  }, [idParam, isCommunityRoute, placeSlugParam, location.pathname, navigate]);
  // Indexing policy: BOTH /park/{place_id} and /place/{slug} are now
  // public, indexable detail pages. Crawlers should be free to rank
  // them in Google SERPs alongside our city + category landing pages.
  //
  // Previously /park/{id} carried a `noindex, follow` to dodge
  // duplicate-content competition with Google Maps listings, but that
  // was hiding ALL place detail pages from Google entirely — including
  // long-tail searches like "<business name> hours" that our pages
  // are the canonical answer for. noindex is now reserved for
  // genuinely-private utility surfaces (/search, /login, /admin/*).
  // Fix #2 Step 4: gate applied further below, after state is declared.

  const { user } = useAuth();
  const [park, setPark] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [myActiveCheckins, setMyActiveCheckins] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Post-check-in share prompt. `null` when hidden; `{ dogNames }` while
  // visible. We trigger it 1.5s after a successful check-in (per spec),
  // which sits ~100ms after the CheckInDrawer's 1.4s auto-close so the
  // two never overlap. State is reset to null when the prompt dismisses.
  const [postCheckinShare, setPostCheckinShare] = useState(null);
  const [favorited, setFavorited] = useState(false);
  const [favBusy, setFavBusy] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  // Signup prompt modal — null when closed, a trigger string when open.
  // Centralised here (vs. one boolean per gated action) so only one
  // prompt can be visible at a time and the trigger drives both the
  // body copy and the GA4 `signup_prompt_shown` event parameter.
  const [signupPrompt, setSignupPrompt] = useState(null);
  const promptSignup = (trigger) => setSignupPrompt(trigger);

  // null = still loading, false = loaded OK, true = the /parks/<id> fetch
  // failed (most commonly 404 — the place id was malformed, deleted, or
  // the manual entry isn't seeded on this environment). Without this we
  // sat on "Loading park…" forever on any error, which confused users
  // who clicked through a stale link or hit a manual-listing URL on an
  // env where the seed hadn't run yet.
  const [loadFailed, setLoadFailed] = useState(false);
  // Derived from URL slug params — used by the `!park` render branch
  // below to show a substantive fallback (name + city + state) when
  // the API fetch is blocked or in flight. Uses ONLY the router
  // params — no dependency on any prerender-embedded seed, so this
  // works for every /{cat}/{city}/{slug} URL regardless of whether
  // the static file was emitted.
  //   - placeSlugParam:  "remrock-farms-veterinary-services-tribpk"
  //     → strip the 6-char id-hash suffix if present → title-case
  //     → "Remrock Farms Veterinary Services"
  //   - citySlug (from window.location, not a param):
  //     "plymouth-mi" → "Plymouth" + state "MI"
  //
  // The 6-char suffix is `-<lowercase alnum>{6}` — a hash of the
  // Google place_id used to guarantee slug uniqueness across cities.
  // Only strip it when the shape matches; community slugs may end in
  // a real word (e.g. "-park"), which we leave intact.
  const seededFromSlug = useMemo(() => {
    if (!placeSlugParam) return null;
    const suffixRe = /-[a-z0-9]{6}$/i;
    const nameSlug = suffixRe.test(placeSlugParam)
      ? placeSlugParam.replace(suffixRe, "")
      : placeSlugParam;
    const titleCase = (s) =>
      s
        .split("-")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    const name = titleCase(nameSlug);
    // City slug is the second segment of the current path — we can't
    // pull it from useParams here because the canonical route
    // declares `:citySlug` under different names on different route
    // shapes. Parsing the pathname directly is bulletproof and
    // read-only.
    let city = "";
    let state = "";
    if (typeof window !== "undefined") {
      const segs = window.location.pathname.split("/").filter(Boolean);
      if (segs.length === 3) {
        const cityStateSlug = segs[1];
        const stateMatch = cityStateSlug.match(/-([a-z]{2})$/);
        if (stateMatch) {
          state = stateMatch[1].toUpperCase();
          city = titleCase(cityStateSlug.slice(0, -3));
        }
      }
    }
    return { name, city, state };
  }, [placeSlugParam]);

  // Indexability gate — kept in sync with the `sitemap_places`
  // completeness rule in backend/routes/places.py (9/18/2026 update)
  // AND with the fetch-blocked failure mode (9/2026 fix, this block).
  //
  // The static shell that prerender.js writes for every place page
  // ships with `<meta name="robots" content="index, follow">` baked
  // in. This hook only OVERRIDES that tag — it flips to
  // `noindex, follow` when we've actually loaded park data AND that
  // data fails the completeness rule. In every other case
  // (park === null pre-load, or loadFailed on error) we DEFER to the
  // prerender-baked tag — critical because Googlebot cannot fetch
  // `/api/*` per robots.txt (`Disallow: /api/` — kept intentionally
  // narrow so /api/parks doesn't trigger billable Google Places
  // refreshes or /api/photo doesn't burn photo-fetch quota on
  // crawler traffic). Before this fix, a blocked API fetch left
  // `park === null` and the client silently flipped every place page
  // to noindex on hydration — reproducible with Playwright + Googlebot
  // UA + /api/* aborted.
  //
  // Rule (when park HAS loaded):
  //   community source → indexable (UGC-native)
  //   otherwise → non-empty city AND state AND ≥1 content signal
  //     (phone | website | non-empty opening_hours | google_reviews[0]
  //      | photos[0])
  const hasContentSignal = park
    ? (
        !!park.phone ||
        !!park.website ||
        (park.opening_hours && (
          Array.isArray(park.opening_hours)
            ? park.opening_hours.length > 0
            : (typeof park.opening_hours === "object"
                ? Object.keys(park.opening_hours).length > 0
                : !!park.opening_hours)
        )) ||
        (Array.isArray(park.google_reviews) && park.google_reviews.length > 0) ||
        (Array.isArray(park.photos) && park.photos.length > 0)
      )
    : false;
  const parkFailsCompletenessRule = park
    ? (park.source === "community"
        ? false
        : !(!!park.city && !!park.state && hasContentSignal))
    : false;
  // Only override the prerender-baked tag when we have proof the
  // page should NOT be indexed. `!park` (loading or fetch-blocked) →
  // useNoIndex(false) → hook is a no-op → prerender's index,follow
  // stays intact. Same when the fetch failed with a network/robots
  // error: without the doc we can't say the page is thin, so we
  // trust the prerender-time decision.
  // Permanently closed pages stay reachable but are never indexed.
  // Shared index rule (src/lib/seoIndexRules.js) — same one the
  // prerender uses for the sitemap and the baked robots tag.
  const failsIndexRule = park ? !isPlaceIndexable(park, reviews.length) : false;
  useNoIndex(parkFailsCompletenessRule || failsIndexRule);

  const loadAll = async () => {
    const [p, c, r] = await Promise.all([
      api.get(`/parks/${id}`),
      api.get(`/parks/${id}/checkins`),
      api.get(`/parks/${id}/reviews`),
    ]);
    setPark(p.data);
    setCheckins(c.data);
    setReviews(r.data);
  };

  const loadMyActive = async () => {
    try {
      const { data } = await api.get("/checkins/active");
      setMyActiveCheckins(data);
    } catch {
      setMyActiveCheckins([]);
    }
  };

  useEffect(() => {
    setLoadFailed(false);
    loadAll().catch((err) => {
      // 404 → bad / missing place id (most common). Any other error
      // (network, 500) → same UX: surface a "not found" message with
      // a back link rather than holding a perma-spinner.
      setLoadFailed(true);
    });
  }, [id]);

  // Funnel instrumentation — fire `park_detail_viewed` once per
  // (mount × park_id), gated on `park` being loaded so name + category
  // are populated. React 18's StrictMode double-invokes the effect in
  // dev, so we cache the last-fired id on a ref to guarantee exactly
  // one event per real navigation.
  const viewedIdRef = React.useRef(null);
  useEffect(() => {
    if (!park || !park.id) return;
    if (viewedIdRef.current === park.id) return;
    viewedIdRef.current = park.id;
    track("park_detail_viewed", {
      park_id: park.id,
      park_name: park.name,
      category: park.place_type || "park",
    });
  }, [park?.id, park?.name, park?.place_type]);

  // Set <title> + meta description per-place. Without this the title
  // carries over from whatever the user came from (homepage, search,
  // city hub) — which leaves /place/{slug} pages stamped with the
  // homepage's generic title for crawlers and browser tabs. We mutate
  // the DOM directly (project pattern — no react-helmet here).
  useEffect(() => {
    if (!park) return;
    const cityState = park.city
      ? `${park.city}${park.state ? ", " + park.state : ""}`
      : "";
    const t = cityState
      ? `${park.name} (${cityState}) | Pawground`
      : `${park.name} | Pawground`;
    document.title = t;
    const descSource = park.why_dog_friendly || park.description || "";
    const desc = descSource
      ? `${descSource.slice(0, 155)}${descSource.length > 155 ? "…" : ""}`
      : `Find ${park.name} on Pawground — a dog-friendly place in ${cityState || "your area"}.`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", desc);

    // Client-side canonical link update. The raw SPA shell hardcodes
    // `<link rel="canonical" href="https://pawground.app/">` so every
    // route that isn't SSR-handled inherits the homepage canonical
    // until React can rewrite it. `parse_path` in seo_renderer.py
    // recognizes /{cat}/{city} and /{city} but NOT the 3-segment
    // /{cat}/{city}/{slug} place-detail path, and never handled
    // /park/{id} at all. Both routes end up serving the shell as-is
    // to bots on first byte. Once React hydrates, `placeUrl(park)`
    // resolves to the correct place-detail path, and we overwrite
    // the link tag here so any downstream JS-executing bot
    // (Googlebot, Bingbot) reads the real canonical before de-indexing
    // us as a duplicate of the homepage. Same pattern as the
    // meta[name=description] and og:image updates immediately above.
    // 9/6/2026 fix.
    const canonicalUrlAbs = `${SITE_URL}${placeUrl(park)}`;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonicalUrlAbs);
    // og:url and twitter:url should track the canonical too — a
    // Facebook or Twitter share-card render for a manual listing
    // would otherwise carry the homepage URL and lose the deep link.
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", canonicalUrlAbs);
    const twUrl = document.querySelector('meta[name="twitter:url"]');
    if (twUrl) twUrl.setAttribute("content", canonicalUrlAbs);

    // Per-place og:image override — when the place has a cached
    // Google photo, swap the global Pawground og:image (logo) for
    // the actual park photo so iMessage / Facebook / X show the
    // real location in shared-link previews. Falls back silently
    // when there's no photo. Note we also update twitter:image and
    // strip the static 1200x630 dimension hints since the real
    // photo aspect ratio almost certainly differs.
    const firstPhotoRef = park.photos?.[0]?.ref;
    if (firstPhotoRef) {
      const imageUrl = photoUrl(firstPhotoRef, 1200);
      const setMeta = (sel, attr, value) => {
        const el = document.querySelector(sel);
        if (el) el.setAttribute(attr, value);
      };
      setMeta('meta[property="og:image"]', "content", imageUrl);
      setMeta('meta[name="twitter:image"]', "content", imageUrl);
      setMeta(
        'meta[property="og:image:alt"]',
        "content",
        `${park.name} — dog-friendly place on Pawground`
      );
      const w = document.querySelector('meta[property="og:image:width"]');
      const h = document.querySelector('meta[property="og:image:height"]');
      if (w) w.remove();
      if (h) h.remove();
    }
  }, [park?.id, park?.name, park?.city, park?.state, park?.why_dog_friendly, park?.description, park?.photos]);

  // Fetch 5-6 nearby places in the same category for the "More
  // {category} near here" internal-linking section. Reuses the existing
  // `/api/places?lat=&lng=` endpoint that the search page uses — no new
  // backend needed. Skips network unless we have lat/lng + a place_type.
  //
  // `source: "seo"` (added 9/3/2026) forces the backend to serve this
  // request entirely from MongoDB cache — no Google call can fire on
  // miss. Detail pages are canonical SEO surfaces (Google indexes
  // `/dog-parks/tampa-fl/deputy-kotfila` and returns it in SERPs), so
  // they must obey the same "cached-only or render less" rule as city
  // and category hubs. Without this flag a crawler sweep across ~50k
  // detail pages pinned us at Google's daily quota (750/750) because
  // each unique park lat/lng was a fresh cache miss that fired a live
  // nearbysearch. The backend's SEO branch has a matching lenient
  // fallback (services/places.py) that reads directly from `db.parks`
  // when both nearby-cache tiers miss, so the "More near here" block
  // still renders from our own 93k-row geo-indexed dataset instead of
  // going blank.
  useEffect(() => {
    if (!park?.lat || !park?.lng || !park?.place_type) return;
    api
      .get("/places", {
        params: {
          lat: park.lat,
          lng: park.lng,
          radius: 8000,
          category: park.place_type,
          limit: 8,
          source: "seo",
        },
      })
      .then((r) => {
        const list = (r.data || []).filter((p) => p.id !== park.id).slice(0, 5);
        setNearbyPlaces(list);
        // Closed pages need alternatives: fall back to the same category in the city.
        if (park.business_status === "CLOSED_PERMANENTLY" && list.length < 3 && park.city && park.state) {
          return api
            .get("/places", {
              params: { q: `${park.city}, ${park.state}`, category: park.place_type, limit: 8, source: "seo" },
            })
            .then((r2) => {
              const extra = (r2.data || []).filter((p) => p.id !== park.id && !list.some((x) => x.id === p.id));
              setNearbyPlaces([...list, ...extra].slice(0, 5));
            });
        }
      })
      .catch(() => setNearbyPlaces([]));
  }, [park?.id, park?.lat, park?.lng, park?.place_type, park?.business_status, park?.city, park?.state]);

  useEffect(() => {
    if (user) {
      api.get("/dogs/mine").then((r) => setDogs(r.data)).catch(() => {});
      loadMyActive();
      api.get(`/favorites/${id}`).then((r) => setFavorited(!!r.data?.favorited)).catch(() => setFavorited(false));
    } else {
      setDogs([]);
      setMyActiveCheckins([]);
      setFavorited(false);
    }
  }, [user, id]);

  const toggleFavorite = async () => {
    if (!user) {
      promptSignup("save");
      return;
    }
    setFavBusy(true);
    const next = !favorited;
    setFavorited(next); // optimistic
    try {
      if (next) {
        await api.post(`/favorites/${id}`);
        track("park_saved", { park_id: park.id, park_name: park.name });
        toast.success("Saved to your parks");
      } else {
        await api.delete(`/favorites/${id}`);
        toast("Removed from saved parks");
      }
    } catch (e) {
      setFavorited(!next); // rollback
      toast.error(e?.response?.data?.detail || "Could not update save");
    } finally {
      setFavBusy(false);
    }
  };

  // Most-recent activity timestamp across user check-ins, reviews,
  // busy reports, and the cache hydration date. Drives:
  //   1. The visible "Last updated …" line below the address
  //   2. `dateModified` in the LocalBusiness JSON-LD (freshness signal
  //      for Google so they don't treat the page as stale even when
  //      Pawground hasn't changed Google's underlying place data).
  // Falls through gracefully — when nothing is set we return null
  // and the UI/JSON-LD both omit the field. MUST stay above the
  // `if (!park) return …` guard or React's rules-of-hooks complains.
  const lastUpdated = useMemo(() => {
    if (!park) return null;
    const candidates = [];
    if (park.busy_reported_at) candidates.push(park.busy_reported_at);
    if (park.cached_at) candidates.push(park.cached_at);
    for (const c of checkins) {
      if (c?.checked_in_at) candidates.push(c.checked_in_at);
    }
    for (const r of reviews) {
      if (r?.created_at) candidates.push(r.created_at);
    }
    let bestDate = null;
    for (const v of candidates) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime()) && (!bestDate || d > bestDate)) {
        bestDate = d;
      }
    }
    return bestDate;
  }, [park, checkins, reviews]);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdated) return null;
    const ageMs = Date.now() - lastUpdated.getTime();
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    if (ageMs > THIRTY_DAYS) {
      return `Last updated ${format(lastUpdated, "MMM d, yyyy")}`;
    }
    return `Last updated ${formatDistanceToNow(lastUpdated, { addSuffix: true })}`;
  }, [lastUpdated]);

  if (!park) {
    // 9/2026 fix (Google Live URL Test regression): whether we're
    // pre-fetch (`!loadFailed`) or post-fetch-failure (`loadFailed`),
    // render the SAME substantive placeholder so the visible page
    // never collapses to "Loading…" or a "Place not found" error
    // screen. Both cases would leave Googlebot staring at an
    // effectively empty page (since /api/* is blocked for its UA),
    // which combined with the previous noindex-until-data-loaded bug
    // was catastrophic for indexing.
    //
    // Content is derived from the URL slug params (see `seededFromSlug`
    // useMemo above) — NO dependency on any prerender-emitted seed,
    // so this works for every /{cat}/{city}/{slug} URL regardless of
    // whether a static file was pre-generated. Community
    // `/place/:slug` and legacy `/park/:id` routes fall through to
    // the generic "Loading place…" text with no city.
    const displayName = seededFromSlug?.name || "Loading place…";
    const cityLine = seededFromSlug
      ? [seededFromSlug.city, seededFromSlug.state].filter(Boolean).join(", ")
      : "";
    return (
      <div
        className="max-w-2xl mx-auto px-4 py-16"
        data-testid={loadFailed ? "park-detail-load-failed" : "park-detail-loading"}
      >
        <p className="text-sm mb-4">
          <Link to="/search" className="text-brand-forest font-semibold hover:underline">
            ← All places
          </Link>
        </p>
        <h1 className="font-heading text-4xl font-bold text-brand-ink mb-3">
          {displayName}
        </h1>
        {cityLine && (
          <p className="text-brand-ink/70 mb-4" data-testid="park-detail-fallback-city">
            {cityLine}
          </p>
        )}
        <p className="text-brand-ink/70">
          Real check-ins, ratings, reviews, and live busy reports from Pawground&apos;s
          community of dog parents.
        </p>
        {loadFailed && (
          <p
            className="text-brand-ink/50 text-sm mt-6"
            data-testid="park-detail-load-failed-note"
          >
            Live details are unavailable right now — try again in a moment.
          </p>
        )}
      </div>
    );
  }

  // Closed places never show an "Open now" / "Closed" hours pill.
  const open = park.business_status === "CLOSED_PERMANENTLY" ? null : isOpenNow(todayHours(park));
  // Delivery-only listings (visit_type=="delivery_only") suppress the
  // walk-in UI: no Get Directions button, no Open Now / Closed pill,
  // and the address line is replaced with a "Delivery / online only"
  // label. Contact channels (phone / email / website / socials) still
  // render normally — the whole point is people should reach out, just
  // not drive to a storefront. See models/parks.py::Park.visit_type
  // for the field spec.
  const isDeliveryOnly = park.visit_type === "delivery_only";
  // Service-area businesses (in-home trainers, mobile groomers, etc.)
  // have no walk-in location. Suppress the same UI as delivery-only:
  // no map pin, no Get Directions, no Open Now/Closed pill. Keyed off
  // an explicit `visit_type === "service_area"` flag (added 9/6/2026)
  // rather than the earlier `!park.address` heuristic — the heuristic
  // couldn't distinguish "legitimately no address" from "we haven't
  // ingested the address yet" and mis-classified listings that had
  // synthetic marker strings in the `address` field. The taxonomy
  // is now: walk_in | delivery_only | service_area | both. See
  // backend/models/parks.py::Park.visit_type for the spec.
  const isServiceArea = park.visit_type === "service_area";
  // Community-submitted places skip Google-specific UI (photo carousel,
  // star rating, open/closed pill, Google reviews, hours sidebar). The
  // backend marks them with `source="community"` — single source of truth.
  const isCommunity = park.source === "community";

  // Resolve the city this place belongs to so we can show a hub link
  // on the breadcrumb + a "More in {city}" CTA at the bottom. May be
  // null for places in cities we don't have a hub for yet.
  const cityHub = cityFromPlace(park);
  const placeCategory = getCategory(park.place_type || "park");

  const dogsCheckedInHere = myActiveCheckins.filter((c) => c.park_id === park.id);
  const allMyDogsHere = user && dogs.length > 0 && dogs.every((d) => dogsCheckedInHere.some((c) => c.dog_id === d.id));

  const handleCheckinClick = () => {
    if (!user) {
      promptSignup("checkin");
      return;
    }
    const style = CHECKIN_STYLE[park.place_type] || "dog";
    if (style === "none") return; // button should be hidden
    if (style === "simple") {
      // No dog selection — just confirm with a POST
      api
        .post("/checkins", { park_id: park.id, dog_ids: [] })
        .then((res) => {
          track("check_in_completed", { park_id: park.id, park_name: park.name, dog_count: 0, simple: true });
          toast.success("You're checked in!", { description: "Your check-in auto-expires in 2 hours." });
          loadAll().catch(() => {});
          // Per spec: wait 1.5s after success before nudging the user
          // to share. Simple check-ins have no drawer to wait on, so
          // 1.5s flat is the right delay.
          setTimeout(
            () => setPostCheckinShare({ dogNames: [], userStats: res?.data?.user_stats || null }),
            1500,
          );
        })
        .catch((e) => toast.error(e?.response?.data?.detail || "Could not check in"));
      return;
    }
    if (dogs.length === 0) {
      // Logged in but no dog profile (e.g. skipped onboarding). Show
      // the hard-required signup-style prompt — `needs_dog_profile`
      // routes the CTA to /onboarding and hides "Maybe Later" since
      // a dog profile is non-negotiable for dog-mode check-ins.
      promptSignup("needs_dog_profile");
      return;
    }
    // Funnel instrumentation — fires exactly when the drawer is about
    // to open (auth + dog-profile guards already passed). Lets the
    // funnel distinguish "opened check-in intent" from
    // "check_in_completed" so we can measure drawer abandonment
    // separately from friction points before the drawer.
    track("checkin_drawer_opened", { park_id: park.id });
    setDrawerOpen(true);
  };

  const confirmDogFriendly = async () => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(placeUrl(park))}`);
      return;
    }
    try {
      const { data } = await api.post(`/places/${park.id}/confirm-dog-friendly`);
      setPark((p) => ({ ...p, verified_count: data.verified_count, verified_by_me: data.verified_by_me }));
      toast.success(data.verified_by_me ? "Thanks for confirming!" : "Confirmation removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save confirmation");
    }
  };

  const submitReview = async ({
    rating, cleanliness, crowdedness, would_return, text,
    price_rating, staff_rating, wait_rating,
  }) => {
    if (!user) {
      promptSignup("review");
      return;
    }
    setSubmittingReview(true);
    try {
      await api.post("/reviews", {
        park_id: park.id,
        rating,
        text: text || "",
        cleanliness,
        crowdedness,
        would_return,
        // Service-category sub-ratings. The form only sends these for
        // vet/groomer/boarding/trainer; for other categories they're
        // undefined and serialize away to nothing.
        price_rating: price_rating ?? null,
        staff_rating: staff_rating ?? null,
        wait_rating:  wait_rating  ?? null,
        dog_id: dogs[0]?.id,
      });
      track("review_submitted", {
        park_id: park.id,
        park_name: park.name,
        rating,
        cleanliness,
        crowdedness,
        would_return,
        price_rating: price_rating ?? null,
        staff_rating: staff_rating ?? null,
        wait_rating:  wait_rating  ?? null,
      });
      toast.success("Thanks for your review!");
      await loadAll();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Review failed");
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <div className="bg-brand-cream min-h-[calc(100vh-4rem)] pb-24">
      <JsonLd
        schema={buildPlaceSchema(park, {
          reviews,
          lastModifiedIso: lastUpdated ? lastUpdated.toISOString() : null,
        })}
        id="park-detail"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        {/* Breadcrumb trail (emits BreadcrumbList JSON-LD as well). When
            we know the city hub we link it ("Tampa") and the category
            ("Dog Parks in Tampa") so users + Google understand the
            hierarchy. */}
        <Breadcrumbs
          items={[
            ...(cityHub
              ? [
                  { name: `${cityHub.name}, ${cityHub.state}`, to: cityUrl(cityHub) },
                  { name: `${placeCategory.seoLabel} in ${cityHub.name}`, to: cityCategoryUrl(cityHub, placeCategory.urlSlug) },
                ]
              : [{ name: "Search", to: "/search" }]),
            { name: park.name },
          ]}
        />

        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-brand-ink/60 hover:text-brand-forest transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to parks
        </button>

        <div className="grid lg:grid-cols-12 gap-8 mt-4">
          <main className="lg:col-span-8 space-y-8">
            <header className="bg-white rounded-3xl border border-brand-border-warm shadow-warm overflow-hidden">
              {park.photos?.length > 0 && (
                <div data-testid="photo-gallery">
                  <div className="relative w-full aspect-[16/9] sm:aspect-[21/9] bg-brand-light-green">
                    <img
                      key={park.photos[activePhoto].ref}
                      src={photoUrl(park.photos[activePhoto].ref, 1600)}
                      alt={park.name}
                      decoding="async"
                      onLoad={() => markPhotoLoaded(park.photos[activePhoto].ref)}
                      className="w-full h-full object-cover animate-fade-in-up"
                    />
                    {park.photos.length > 1 && (
                      <div className="absolute bottom-2 right-2 text-[10px] uppercase tracking-wider font-bold bg-black/55 text-white rounded-full px-2.5 py-1">
                        {activePhoto + 1} / {park.photos.length}
                      </div>
                    )}
                  </div>
                  {park.photos.length > 1 && (
                    <div className="flex gap-2 px-4 pt-3 overflow-x-auto scrollbar-hide">
                      {park.photos.map((ph, i) => (
                        <button
                          key={ph.ref}
                          data-testid={`photo-thumb-${i}`}
                          onClick={() => setActivePhoto(i)}
                          className={`w-16 h-16 rounded-xl shrink-0 overflow-hidden border-2 transition-all ${
                            i === activePhoto ? "border-brand-forest ring-2 ring-brand-forest/30" : "border-transparent opacity-70 hover:opacity-100"
                          }`}
                        >
                          <img
                            src={photoUrl(ph.ref, 200)}
                            alt={`${park.name} ${i + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            onLoad={() => markPhotoLoaded(ph.ref)}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {(() => {
                      const cat = getCategory(park.place_type || "park");
                      const Icon = cat.icon;
                      return (
                        <span
                          data-testid="park-detail-category-badge"
                          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        >
                          {Icon && <Icon className="w-3 h-3" strokeWidth={2.6} />}
                          {cat.label}
                        </span>
                      );
                    })()}
                    {isCommunity && <CommunityBadge size="lg" />}
                  </div>
                  <ClosedNotice
                    status={park.business_status}
                    alternatives={nearbyPlaces}
                    moreUrl={cityHub && placeCategory ? cityCategoryUrl(cityHub, placeCategory.urlSlug) : null}
                    moreLabel={cityHub && placeCategory ? `See all ${placeCategory.seoLabel.toLowerCase()} in ${cityHub.name}` : ""}
                  />
                  <h1
                    data-testid="park-detail-name"
                    className="font-heading text-3xl sm:text-4xl font-black text-brand-ink tracking-tight leading-tight"
                  >
                    {park.name}
                  </h1>
                  <div className="flex items-center gap-1.5 mt-2 text-sm text-brand-ink/70">
                    <MapPin className="w-4 h-4" />{" "}
                    {isDeliveryOnly
                      // Delivery-only: hide the unverified street
                      // address so it doesn't read as "come visit us."
                      // Fall back to city, state which is verified and
                      // useful (SEO surfaces it too via `city`).
                      ? [park.city, park.state].filter(Boolean).join(", ")
                      : park.address}
                  </div>
                  {lastUpdatedLabel && (
                    <div
                      data-testid="last-updated-label"
                      className="mt-1 font-body"
                      style={{ color: "#7A7A60", fontSize: "12px" }}
                    >
                      {lastUpdatedLabel}
                    </div>
                  )}
                  {/* Google-attributed rating strip — only for manual
                      listings that have been explicitly linked to a
                      Google Business Profile via `google_place_id`
                      AND have a non-zero Google review count. Uses
                      the cached `user_rating_count` (refreshed at
                      most once every 30 days via
                      services/google_places_new.py). Overrides the
                      generic `!isCommunity` strip below because the
                      strip below renders park.reviews_count (which
                      is the Pawground count) — for a Google-linked
                      manual listing we want the Google count in the
                      header instead. The Pawground count still shows
                      further down in the reviews-section header. */}
                  {park.user_rating_count > 0 && (
                    <div
                      className="flex items-center gap-3 mt-4"
                      data-testid="google-rating-strip"
                    >
                      <div className="flex items-center gap-1">
                        <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                        <span
                          className="font-bold text-lg text-brand-ink"
                          data-testid="google-rating-value"
                        >
                          {park.rating.toFixed(1)}
                        </span>
                        <span
                          className="text-sm text-brand-ink/60"
                          data-testid="google-rating-count"
                        >
                          · {park.user_rating_count} Google review
                          {park.user_rating_count === 1 ? "" : "s"}
                        </span>
                      </div>
                      {isDeliveryOnly ? (
                        <span
                          data-testid="delivery-only-badge"
                          className="px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-brand-light-green text-brand-forest"
                        >
                          Delivery / online only
                        </span>
                      ) : open === null ? null : (
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                          open ? "bg-brand-light-green text-brand-forest" : "bg-red-50 text-red-600"
                        }`}>
                          {open ? "Open now" : "Closed"}
                        </span>
                      )}
                    </div>
                  )}
                  {!isCommunity && !(park.user_rating_count > 0) && (
                    <div className="flex items-center gap-3 mt-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                        <span className="font-bold text-lg text-brand-ink">{park.rating.toFixed(1)}</span>
                        <span className="text-sm text-brand-ink/60">({park.reviews_count} reviews)</span>
                      </div>
                      {isDeliveryOnly ? (
                        // Delivery-only businesses have no store hours
                        // to open/close — surface the delivery mode
                        // instead. Same visual weight as the pill it
                        // replaces so the info-row rhythm holds.
                        <span
                          data-testid="delivery-only-badge"
                          className="px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-brand-light-green text-brand-forest"
                        >
                          Delivery / online only
                        </span>
                      ) : open === null ? (
                        // Hours unknown/unparseable — render no pill
                        // (9/6/2026 fix). Applies to service-area
                        // businesses with no walk-in hours and to
                        // listings whose hours are "By appointment"-
                        // style strings that isOpenNow can't parse.
                        // Rendering nothing is deliberate: "Hours vary"
                        // would suggest we know the hours vary, which
                        // we don't — we just don't have parseable
                        // hours. Absence is more honest than a label.
                        null
                      ) : (
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                          open ? "bg-brand-light-green text-brand-forest" : "bg-red-50 text-red-600"
                        }`}>
                          {open ? "Open now" : "Closed"}
                        </span>
                      )}
                    </div>
                  )}
                  {isCommunity && park.reviews_count > 0 && (
                    // For community places we only surface the
                    // *Pawground* review count (no Google rating). The
                    // star rating is rendered by the reviews-section
                    // header below as users add reviews.
                    <div className="flex items-center gap-1 mt-4 text-sm text-brand-ink/70">
                      <Star className="w-4 h-4 text-brand-forest" />
                      <span className="font-semibold">{park.reviews_count}</span>
                      <span>Pawground review{park.reviews_count === 1 ? "" : "s"}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {park.phone && (
                    <a
                      data-testid="phone-btn"
                      href={`tel:${park.phone.replace(/[^\d+]/g, "")}`}
                      className="bg-white border-2 border-brand-border-warm text-brand-ink font-semibold rounded-full px-5 py-3.5 hover:border-brand-forest hover:text-brand-forest transition-all active:scale-95 flex items-center gap-2"
                      title={park.phone}
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </a>
                  )}
                  {park.website && (
                    <a
                      data-testid="website-btn"
                      href={park.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        // Fire-and-forget sponsor attribution when
                        // this detail page belongs to a sponsor's
                        // linked_place. `click_source='detail'` is
                        // the third attribution surface after 'band'
                        // and 'pinned', so paid clicks from the
                        // deep-detail page are counted separately.
                        if (park.is_sponsored && park.sponsor_slug) {
                          try {
                            api.post(`/sponsors/${park.sponsor_slug}/click`, {
                              referrer_path: window.location.pathname,
                              click_source: "detail",
                            });
                          } catch (_) { /* best-effort */ }
                        }
                      }}
                      className="bg-white border-2 border-brand-border-warm text-brand-ink font-semibold rounded-full px-5 py-3.5 hover:border-brand-forest hover:text-brand-forest transition-all active:scale-95 flex items-center gap-2"
                    >
                      <Globe className="w-4 h-4" />
                      Website
                    </a>
                  )}
                  {/* Email — only present on manual service-business
                      listings. Surfaces as a `mailto:` so the host's
                      default mail client opens; falls back to nothing
                      for Google-sourced parks where the field is "". */}
                  {park.email && (
                    <a
                      data-testid="email-btn"
                      href={`mailto:${park.email}`}
                      className="bg-white border-2 border-brand-border-warm text-brand-ink font-semibold rounded-full px-5 py-3.5 hover:border-brand-forest hover:text-brand-forest transition-all active:scale-95 flex items-center gap-2"
                      title={park.email}
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </a>
                  )}
                  {/* Instagram — only on manual listings where the host
                      uses IG as their primary storefront (e.g. Pop!
                      Grooming PR). Stripped from the API response for
                      Google-sourced parks, so the field is undefined
                      and this branch is skipped. */}
                  {park.instagram && (
                    <a
                      data-testid="instagram-btn"
                      href={park.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white border-2 border-brand-border-warm text-brand-ink font-semibold rounded-full px-5 py-3.5 hover:border-brand-forest hover:text-brand-forest transition-all active:scale-95 flex items-center gap-2"
                      aria-label="Instagram"
                      title="Instagram"
                    >
                      <Instagram className="w-4 h-4" />
                      Instagram
                    </a>
                  )}
                  {park.facebook && (
                    <a
                      data-testid="facebook-btn"
                      href={park.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white border-2 border-brand-border-warm text-brand-ink font-semibold rounded-full px-5 py-3.5 hover:border-brand-forest hover:text-brand-forest transition-all active:scale-95 flex items-center gap-2"
                      aria-label="Facebook"
                      title="Facebook"
                    >
                      <Facebook className="w-4 h-4" />
                      Facebook
                    </a>
                  )}
                  {!isDeliveryOnly && !isServiceArea && (
                    // Suppressed for delivery-only (no walk-in
                    // storefront) AND service-area businesses
                    // (no fixed address, catchment covers multiple
                    // cities). Both paths would either point to a
                    // residential anchor lat/lng or a P.O. Box —
                    // misleading to the user. 9/6/2026 fix.
                    <a
                      data-testid="directions-btn"
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(park.address || `${park.lat},${park.lng}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white border-2 border-brand-border-warm text-brand-ink font-semibold rounded-full px-5 py-3.5 hover:border-brand-forest hover:text-brand-forest transition-all active:scale-95 flex items-center gap-2"
                    >
                      <Navigation className="w-4 h-4" />
                      Get directions
                    </a>
                  )}
                  <ShareButton place={park} />
                  <button
                    data-testid="favorite-toggle-btn"
                    onClick={toggleFavorite}
                    disabled={favBusy}
                    aria-pressed={favorited}
                    aria-label={favorited ? "Remove from saved parks" : "Save park"}
                    className={`rounded-full w-12 h-12 flex items-center justify-center border transition-all active:scale-95 ${
                      favorited
                        ? "bg-rose-50 border-rose-300 text-rose-500 hover:bg-rose-100"
                        : "bg-white border-brand-border-warm text-brand-ink/70 hover:border-brand-forest hover:text-brand-forest"
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${favorited ? "fill-rose-500" : ""}`} strokeWidth={favorited ? 2.4 : 2} />
                  </button>
                  {(() => {
                    const style = CHECKIN_STYLE[park.place_type] || "dog";
                    if (style === "none") return null;
                    const label =
                      style === "simple"
                        ? (allMyDogsHere ? "You're here!" : "I'm here with my dog")
                        : (allMyDogsHere
                            ? (dogs.length === 1 ? `${dogs[0].name} is checked in!` : "You're checked in!")
                            : "Check in here");
                    return (
                      <button
                        data-testid="checkin-open-btn"
                        onClick={handleCheckinClick}
                        disabled={allMyDogsHere}
                        className="bg-brand-forest text-white font-semibold rounded-full px-6 py-3.5 hover:bg-brand-forest-dark transition-colors active:scale-95 shadow-warm flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <PawPrint className="w-5 h-5" />
                        {label}
                      </button>
                    );
                  })()}
                </div>
              </div>

              {isCommunity && park.why_dog_friendly && (
                <div
                  data-testid="why-dog-friendly-section"
                  className="mt-5 rounded-2xl border p-5"
                  style={{ backgroundColor: "#E8F2EC", borderColor: "#D4C9B0" }}
                >
                  <div
                    className="font-body font-semibold mb-2"
                    style={{ color: "#4A7C59", fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase" }}
                  >
                    Why it's dog-friendly
                  </div>
                  <p
                    className="font-body"
                    style={{ color: "#1A1A0F", fontSize: "15px", lineHeight: 1.6 }}
                  >
                    {park.why_dog_friendly}
                  </p>
                  {park.submitted_by_name && (
                    <div
                      className="mt-3 font-body"
                      style={{ color: "#7A7A60", fontSize: "12px" }}
                    >
                      Submitted by {park.submitted_by_name}
                    </div>
                  )}
                </div>
              )}

              {!isCommunity && (
                <p className="text-[15px] text-brand-ink/80 mt-5 leading-relaxed max-w-2xl whitespace-pre-line">
                  {park.description}
                </p>
              )}

              <div className="flex flex-wrap gap-2 mt-5">
                {park.amenities.map((a) => (
                  <span
                    key={a}
                    className="text-xs font-bold uppercase tracking-wider text-brand-forest bg-brand-light-green px-3 py-1.5 rounded-full"
                  >
                    {a === "Small dog section" ? "Small dogs" : a}
                  </span>
                ))}
              </div>

              {/* Services offered — used by `source="manual_add"` business
                  listings (mobile trainers, dog walkers, pet sitters) to
                  surface their full service menu beyond the primary
                  category. Styled distinctly from amenities (deeper
                  green tone + leading "Services:" label) so the
                  business-meaning is unambiguous on the detail page. */}
              {park.services_offered?.length > 0 && (
                <div
                  className="flex flex-wrap items-center gap-2 mt-3"
                  data-testid="services-offered-row"
                >
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-ink/60 mr-1">
                    Services
                  </span>
                  {park.services_offered.map((s) => (
                    <span
                      key={s}
                      className="text-xs font-bold uppercase tracking-wider text-white bg-brand-forest px-3 py-1.5 rounded-full"
                      data-testid={`service-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              )}

              {/* Pricing block — surfaced for manual listings (currently
                  groomers / mobile service businesses) that ship a
                  structured `pricing` array. Two-column readable layout
                  with each row showing the label on the left and the
                  price value on the right in primary green. Falls back
                  to nothing when the field is absent. */}
              {Array.isArray(park.pricing) && park.pricing.length > 0 && (
                <div
                  className="mt-5 max-w-2xl rounded-2xl border border-brand-border-warm bg-white p-5"
                  data-testid="pricing-block"
                >
                  <div className="text-xs font-bold uppercase tracking-wider text-brand-ink/60 mb-3">
                    Pricing
                  </div>
                  <ul className="divide-y divide-brand-border-warm">
                    {park.pricing.map((p, i) => (
                      <li
                        key={`${p.label}-${i}`}
                        className="flex items-baseline justify-between gap-4 py-2.5 font-body"
                        data-testid={`pricing-row-${i}`}
                      >
                        <span className="text-sm text-brand-ink/85">{p.label}</span>
                        <span
                          className="text-sm font-semibold whitespace-nowrap"
                          style={{ color: "#4A7C59" }}
                        >
                          {p.value}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}


              {/* Service area footer — only relevant for service-area
                  businesses without a physical storefront. Lists every
                  city the business covers so visitors who landed on
                  one city page know the same provider can serve them
                  in nearby cities too. */}
              {park.service_areas?.length > 0 && (
                <p
                  className="text-sm text-brand-ink/70 mt-3 max-w-2xl"
                  data-testid="service-areas-line"
                >
                  <span className="font-semibold text-brand-ink">Service area:</span>{" "}
                  {park.service_areas.join(", ")}
                  {park.state ? `, ${park.state}` : ""}
                </p>
              )}

              {/* Booking CTA — when the business provides a direct booking
                  link (Calendly, Square, custom widget) we surface it as
                  the primary action below the contact buttons. Higher
                  conversion than "Website" since it lands users on the
                  booking form, not the homepage. */}
              {park.booking_url && (
                <a
                  href={park.booking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-online-cta"
                  className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full bg-brand-forest text-white text-sm font-bold shadow-warm hover:bg-brand-forest-dark active:scale-95 transition-all"
                >
                  Book Online
                  <span aria-hidden="true">→</span>
                </a>
              )}

              {/* External Google Reviews link — temporary stand-in for
                  businesses that don't have a Google Maps listing we
                  can pull reviews from via the Places API. Surfaces as
                  a secondary outlined button next to Book Online so
                  visitors can still cross-check social proof. Removed
                  automatically once we replace it with native reviews
                  fetched from a real Maps place_id. */}
              {park.google_reviews_url && (
                <a
                  href={park.google_reviews_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="see-google-reviews-cta"
                  className="inline-flex items-center gap-2 mt-3 ml-3 px-6 py-3 rounded-full bg-white text-brand-ink text-sm font-bold border-2 border-brand-border-warm hover:border-brand-forest hover:text-brand-forest active:scale-95 transition-all"
                >
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" strokeWidth={2} />
                  See Reviews on Google
                  <span aria-hidden="true">↗</span>
                </a>
              )}
              </div>
            </header>

            {/* Park activity right now — only for categories where
                check-ins and busyness are meaningful. Service-only
                place types (vet, groomer, boarding, trainer, store)
                have no check-ins by design (`CHECKIN_STYLE === "none"`)
                and don't need a "How busy is it?" widget either —
                you don't crowd-source how busy your vet is. Hiding
                the whole section avoids an empty box on those pages.
                Reviews stay visible for every category (next section). */}
            {["park", "beach", "restaurant", "trail"].includes(park.place_type) && (
            <section className="bg-white rounded-3xl p-6 sm:p-8 border border-brand-border-warm">
              <h2 className="font-heading text-2xl font-bold text-brand-ink mb-1">
                {park.place_type === "park" ? "Park activity right now" : "Who's here now"}
              </h2>
              <p className="text-sm text-brand-ink/60">Check-ins and busy reports auto-expire after 2 hours.</p>

              <div className="mt-5">
                <BusyReport park={park} user={user} onReported={loadAll} onPromptSignup={promptSignup} />
              </div>

              {checkins.length === 0 && (!user || !park.busy_level) ? (
                /* Open empty-state — shown when:
                   - the user is logged in AND no busy_level is set
                     (the original gentle CTA path), OR
                   - the user is logged OUT and no one is checked in
                     (Change 2: skip the blur in the zero-activity case
                     so we don't tease an empty feed; the busy_report
                     signup prompt is a more useful conversion).
                   For logged-out users the busy_report signup prompt
                   reuses the same modal trigger as before — copy and
                   behavior intentionally unchanged. */
                <div
                  className="mt-5 bg-brand-light-green rounded-2xl p-5 text-center"
                  data-testid="activity-empty-state"
                >
                  <Dog className="w-10 h-10 mx-auto text-brand-forest mb-2" />
                  <div className="font-heading text-lg font-semibold text-brand-ink">
                    Report how busy it is!
                  </div>
                  {user ? (
                    <p className="text-sm text-brand-ink/70 mt-1">
                      Tap a busyness option above to let others know how busy the park is.
                    </p>
                  ) : (
                    <p className="text-sm text-brand-ink/70 mt-1">
                      <button
                        type="button"
                        data-testid="activity-empty-signup-link"
                        onClick={() => promptSignup("busy_report")}
                        className="text-brand-forest font-semibold hover:underline"
                      >
                        Sign up
                      </button>{" "}
                      to let others know how many dogs are at this park right now.
                    </p>
                  )}
                </div>
              ) : checkins.length === 0 ? (
                <p className="mt-4 text-sm text-brand-ink/60">
                  No one's checked in yet — be the first to bring a pup!
                </p>
              ) : !user ? (
                // --- Logged-out: blurred preview + signup CTA -----------
                // Show the SHAPE of the activity (so users feel the pulse
                // of the place) but blur the dog names/breeds and replace
                // initials with a paw glyph so they can't read who's
                // here without an account. Clicking anywhere on the
                // teaser fires the `activity` signup prompt.
                <button
                  type="button"
                  data-testid="checkins-feed-locked"
                  onClick={() => promptSignup("activity")}
                  className="block w-full text-left mt-5 group"
                  aria-label="Sign up to see who's checked in"
                >
                  <div
                    className="relative flex gap-4 overflow-hidden pb-4 -mx-6 sm:-mx-8 px-6 sm:px-8 pointer-events-none select-none"
                    aria-hidden
                  >
                    {checkins.slice(0, 5).map((c) => (
                      <div
                        key={c.id}
                        className="shrink-0 w-40 bg-brand-cream rounded-2xl p-4 border border-brand-border-warm flex flex-col items-center text-center filter blur-[6px] saturate-75 opacity-90"
                      >
                        <span className="w-16 h-16 rounded-full bg-brand-forest/80 text-white flex items-center justify-center mb-2">
                          <PawPrint className="w-7 h-7" />
                        </span>
                        <div className="h-3 w-20 rounded-full bg-brand-ink/15 mb-1.5" />
                        <div className="h-2.5 w-16 rounded-full bg-brand-ink/10" />
                        <div className="h-2.5 w-14 rounded-full bg-brand-forest/30 mt-3" />
                      </div>
                    ))}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/55 via-white/30 to-white/85" />
                  </div>
                  <div className="-mt-2 mb-1 mx-auto max-w-md bg-brand-light-green rounded-2xl px-4 py-3 text-center border border-brand-forest/30 shadow-warm">
                    <div className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink">
                      <PawPrint className="w-4 h-4 text-brand-forest" />
                      {checkins.length} dog{checkins.length === 1 ? "" : "s"} here right now
                    </div>
                    <div className="text-xs text-brand-ink/70 mt-1">
                      Sign up to see who's checked in →
                    </div>
                  </div>
                </button>
              ) : (
                <div
                  className="mt-5 flex gap-4 overflow-x-auto pb-4 -mx-6 sm:-mx-8 px-6 sm:px-8 snap-x scrollbar-hide"
                  data-testid="checkins-feed"
                >
                  {checkins.map((c) => {
                    const initials = c.dog_name.slice(0, 1).toUpperCase();
                    return (
                      <div key={c.id} className="shrink-0 snap-start w-40 bg-brand-cream rounded-2xl p-4 border border-brand-border-warm flex flex-col items-center text-center">
                        <Avatar
                          src={c.dog_photo_url || (c.is_simple ? c.user_photo_url : null)}
                          name={c.dog_name}
                          initials={initials}
                          size={64}
                          fontSize={24}
                          className="mb-2"
                          testId={`checkin-photo-${c.id}`}
                        />
                        <div className="font-heading text-base font-semibold text-brand-ink truncate w-full">{c.dog_name}</div>
                        <div className="text-xs text-brand-ink/60 truncate w-full">{c.dog_breed}</div>
                        {/* Dog Journal note — same italic/muted treatment
                            as profile visit history. Hidden when the
                            check-in carries no note. Capped at 2 lines
                            here because the active-checkin card is a
                            tight horizontal carousel slot. */}
                        {c.note && (
                          <div
                            data-testid={`active-checkin-note-${c.id}`}
                            className="mt-1.5 italic w-full"
                            style={{
                              fontSize: 11,
                              color: "#7A7A60",
                              lineHeight: 1.35,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              whiteSpace: "pre-line",
                            }}
                          >
                            {c.note}
                          </div>
                        )}
                        <div className="text-[11px] text-brand-forest font-semibold mt-2">
                          {formatDistanceToNowStrict(new Date(c.checked_in_at))} ago
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
            )}

            {/* Reviews */}
            <section className="bg-white rounded-3xl p-6 sm:p-8 border border-brand-border-warm">
              {(() => {
                /* Personalized review headline + subtext.
                   - User has at least one dog → use their FIRST dog's
                     name in the headline and their FIRST dog's breed
                     in the subtext (per spec).
                   - Logged-out OR skipped-onboarding (no dogs) → fall
                     back to the un-personalized "How was {park}?" plus
                     the generic "dog parents decide" subtext.
                   The legacy plain "Reviews" h2 stays out — Change 3
                   replaces it entirely. */
                const firstDog = user && dogs.length > 0 ? dogs[0] : null;
                const headline = firstDog
                  ? `How was ${park.name} with ${firstDog.name}?`
                  : `How was ${park.name}?`;
                const subtext = firstDog && firstDog.breed
                  ? `Your review helps other ${firstDog.breed} owners decide`
                  : "Your review helps other dog parents decide";
                return (
                  <>
                    <h2
                      data-testid="review-headline"
                      className="font-heading text-2xl font-bold text-brand-ink leading-tight"
                    >
                      {headline}
                    </h2>
                    <p
                      data-testid="review-subtext"
                      className="font-body mt-1"
                      style={{ fontSize: 13, color: "#7A7A60" }}
                    >
                      {subtext}
                    </p>
                  </>
                );
              })()}

              {user ? (
                <QuickReviewForm
                  onSubmit={submitReview}
                  submitting={submittingReview}
                  placeType={park.place_type}
                />
              ) : (
                // Logged-out: tappable placeholder that opens the signup
                // prompt instead of a disabled form. Cleaner UX than a
                // greyed-out form — single tap converts to signup with
                // no abandoned typed text to lose.
                <button
                  type="button"
                  data-testid="review-locked"
                  onClick={() => promptSignup("review")}
                  className="mt-5 w-full text-left rounded-2xl p-4 border hover:border-brand-forest hover:shadow-warm transition-all group"
                  style={{ backgroundColor: "#F5F0E8", borderColor: "#D4C9B0" }}
                >
                  <div className="font-body text-sm font-semibold" style={{ color: "#1A1A0F" }}>
                    How dog-friendly is this place?
                  </div>
                  <div className="mt-2 opacity-60">
                    <PawRating value={0} testIdPrefix="locked-paw" />
                  </div>
                  <div className="mt-3 bg-white border rounded-2xl px-3 py-2.5 text-sm" style={{ borderColor: "#D4C9B0", color: "rgba(26,26,15,0.55)" }}>
                    Been here? Tap to leave a quick review →
                  </div>
                  <div className="flex items-center justify-end mt-3">
                    <span
                      className="font-body font-semibold rounded-full px-5 py-2 text-sm inline-flex items-center gap-1.5 group-hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: "#4A7C59", color: "#FFFFFF" }}
                    >
                      <Send className="w-4 h-4" />
                      Sign up to post
                    </span>
                  </div>
                </button>
              )}

              <div className="mt-6 space-y-4" data-testid="reviews-list">
                {reviews.length === 0 ? (
                  <div className="text-sm text-brand-ink/60">No reviews yet — be the first!</div>
                ) : (
                  reviews.map((r) => <ReviewCard key={r.id} review={r} />)
                )}
              </div>
            </section>

            <GoogleReviewsSection reviews={park.google_reviews} />
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            <WeatherWidget lat={park.lat} lng={park.lng} />

            {!isCommunity && (
              <div className="bg-white rounded-2xl p-5 border border-brand-border-warm shadow-warm">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-ink/50 flex items-center gap-1.5 mb-3">
                  <Clock className="w-3.5 h-3.5" /> Hours
                </div>
                <ul className="space-y-1.5 text-sm">
                  {DAYS.map(([k, label]) => (
                    <li key={k} className="flex justify-between text-brand-ink/80">
                      <span className="font-medium">{label}</span>
                      <span>{park.hours?.[k] || "—"}</span>
                    </li>
                  ))}
                </ul>
                {/* Payment methods — manual service listings only.
                    Rendered as a single muted line below the day grid
                    so it reads as supplementary info, not a primary
                    action. "Pago:" label kept in Spanish to match the
                    EN/ES bilingual treatment used elsewhere on PR
                    listings (Pop! Grooming, Mara's MakeOver). */}
                {park.payment_methods?.length > 0 && (
                  <div
                    data-testid="payment-methods"
                    className="mt-3 pt-3 border-t border-brand-border-warm/60 text-xs text-brand-ink/60"
                  >
                    <span className="font-semibold text-brand-ink/70">Pago:</span>{" "}
                    {park.payment_methods.join(" · ")}
                  </div>
                )}
              </div>
            )}

            {park.rules?.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-brand-border-warm shadow-warm">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-ink/50 mb-3">Park rules</div>
                <ul className="space-y-2 text-sm text-brand-ink/80">
                  {park.rules.map((r, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-brand-forest">•</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!isCommunity && VERIFIABLE_CATEGORIES.has(park.place_type) && (
              <div
                data-testid="verify-card"
                className="bg-white rounded-2xl p-5 border border-brand-border-warm shadow-warm"
              >
                <div className="flex items-center gap-2 text-brand-ink/70 text-[11px] uppercase tracking-[0.18em] font-semibold mb-2">
                  <ShieldCheck className="w-3.5 h-3.5" /> Community verification
                </div>
                <p className="text-sm text-brand-ink/80 leading-relaxed">
                  {park.verified_count > 0
                    ? <><span className="font-bold text-brand-forest">{park.verified_count}</span> {park.verified_count === 1 ? "person has" : "people have"} confirmed this is dog-friendly.</>
                    : <>Google says this place is dog-friendly. Confirm from real experience?</>}
                </p>
                <button
                  type="button"
                  data-testid="verify-btn"
                  onClick={confirmDogFriendly}
                  aria-pressed={park.verified_by_me}
                  className={[
                    "mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-all active:scale-[0.97]",
                    park.verified_by_me
                      ? "bg-brand-forest text-white border-brand-forest hover:bg-brand-forest-dark"
                      : "bg-white text-brand-forest border-brand-forest hover:bg-brand-light-green",
                  ].join(" ")}
                >
                  <ShieldCheck className="w-4 h-4" />
                  {park.verified_by_me ? "You confirmed this" : "Confirm dog-friendly"}
                </button>
              </div>
            )}

            {park.busy_level && (
              <div
                data-testid="busyness-sidebar-summary"
                className="bg-brand-light-green rounded-2xl p-5 border border-brand-forest/20"
              >
                <div className="flex items-center gap-2 text-brand-forest text-sm font-semibold">
                  <Users className="w-4 h-4" />
                  Right now
                </div>
                <div className="mt-2 text-sm text-brand-ink/80">
                  Reported as{" "}
                  <span className="font-semibold text-brand-forest capitalize">{park.busy_level}</span>
                  {park.busy_reported_at && (
                    <>
                      {" · "}
                      {formatDistanceToNow(new Date(park.busy_reported_at), { addSuffix: false })} ago
                    </>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>

        {/* Internal linking: more in this category in this city + 5 nearby
            same-category places. Strengthens topical clusters per SEO
            rules without requiring a new backend endpoint. */}
        {(cityHub || nearbyPlaces.length > 0) && (
          <section
            data-testid="park-internal-links"
            className="mt-10 grid md:grid-cols-2 gap-6"
          >
            {cityHub && (
              <Link
                to={cityCategoryUrl(cityHub, placeCategory.urlSlug)}
                data-testid="park-city-hub-link"
                className="block bg-brand-light-green border border-brand-forest/20 rounded-3xl p-6 hover:border-brand-forest hover:shadow-warm transition-all group"
              >
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-forest mb-2">
                  More in {cityHub.name}
                </div>
                <div className="font-heading text-xl font-bold text-brand-ink group-hover:text-brand-forest transition-colors">
                  All {placeCategory.seoShort} in {cityHub.name}, {cityHub.state}
                </div>
                <span className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-brand-forest group-hover:gap-2 transition-all">
                  See the full {cityHub.name} hub <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            )}
            {nearbyPlaces.length > 0 && (
              <div
                data-testid="park-nearby-places"
                className="bg-white border border-brand-border-warm rounded-3xl p-6 shadow-warm"
              >
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-forest mb-3">
                  Nearby {placeCategory.seoShort}
                </div>
                <ul className="space-y-2.5">
                  {nearbyPlaces.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={placeUrl(p)}
                        data-testid={`park-nearby-${p.id}`}
                        className="flex items-center justify-between gap-3 text-sm text-brand-ink hover:text-brand-forest group"
                      >
                        <span className="truncate font-medium">{p.name}</span>
                        <span className="inline-flex items-center gap-1 text-xs text-brand-ink/50 shrink-0">
                          {p.rating && (
                            <>
                              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                              {p.rating.toFixed(1)}
                            </>
                          )}
                          <ArrowRight className="w-3.5 h-3.5 text-brand-forest opacity-60 group-hover:opacity-100 transition-opacity" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
      </div>

      <CheckInDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        park={park}
        dogs={dogs}
        activeCheckIns={myActiveCheckins}
        onSuccess={async ({ dogNames, userStats, checkinIds } = {}) => {
          await loadAll();
          await loadMyActive();
          // Drawer auto-closes 1.4s after success. Total delay from
          // success-state → prompt-visible is ~1.5s (1.4s drawer close
          // + 100ms buffer) so the prompt never overlaps the vaul
          // drawer's exit animation.
          setTimeout(
            () => setPostCheckinShare({
              dogNames: dogNames || [],
              userStats: userStats || null,
              checkinIds: checkinIds || [],
            }),
            100,
          );
        }}
      />

      <PostCheckinSharePrompt
        open={!!postCheckinShare}
        park={park}
        dogNames={postCheckinShare?.dogNames || []}
        userStats={postCheckinShare?.userStats || null}
        checkinIds={postCheckinShare?.checkinIds || []}
        onClose={() => setPostCheckinShare(null)}
      />

      <SignupPromptModal
        open={!!signupPrompt}
        trigger={signupPrompt || "save"}
        onClose={() => setSignupPrompt(null)}
      />
    </div>
  );
};

export default ParkDetail;
